// app/src/lib/cashflow/spendCategory.js — cost-report allocation for credit-card
// charges shown on the Spending tab, and for unclassified bank outflows the
// operator categorizes from Today's Recent-activity tile (same rule storage).
//
// Each card charge gets a category in this precedence:
//   reconciled COGS  >  per-charge override  >  per-merchant rule  >  keyword guess  >  null
// COGS is normally assigned by the reconciler (a scanned invoice matched to the
// charge), but the operator can ALSO pick COGS by hand for an unscanned
// ingredient run — e.g. a $5 QFC or a Lam Seafood Market charge an employee put
// on the card without a receipt. Rules are keyed by normalized merchant (so
// "Amazon → Supplies" sticks for every Amazon charge) or, for a one-off, by the
// individual charge (the `tx:<plaidTxId>` key set by the UI's "just this charge").

import { db } from '../db.js';
import { getKeywords as getRentKeywords, isRent } from './rent.js';
import { getKeywords as getInsuranceKeywords, isInsurance } from './insurance.js';
import { getKeywords as getExpenseKeywords, categorizeExpense } from './expenseKeywords.js';
import { getKeywords as getTaxKeywords, classifyTax } from './taxes.js';
import { rawTxnName } from './util.js';

// Dropdown taxonomy. ids are stable; labels are English fallbacks (the UI
// localizes via t()). COGS is usually reconciliation-only but is pickable too,
// for unscanned ingredient buys (see header).
export const SPEND_CATEGORIES = Object.freeze([
  { id: 'cogs',           label: 'COGS' },
  { id: 'rent_utilities', label: 'Rent & Utilities' },
  { id: 'supplies',       label: 'Supplies' },
  { id: 'maintenance',    label: 'Maintenance & Repairs' },
  { id: 'insurance',      label: 'Insurance' },
  { id: 'advertising',    label: 'Advertising' },
  { id: 'software',       label: 'Software' },
  { id: 'capex',          label: 'Capital Expenses' },
  { id: 'taxes',          label: 'Taxes' },
  { id: 'other',          label: 'Other' },
]);

// Every category is pickable in the dropdown — including COGS, so an operator
// can hand-classify an unscanned ingredient charge the reconciler never saw.
export const PICKABLE_CATEGORIES = SPEND_CATEGORIES;

const VALID_IDS = new Set(SPEND_CATEGORIES.map((c) => c.id));

export function normalizeMerchant(name) {
  // Drop store-number / reference suffixes so a per-merchant rule covers every
  // location: "IKEA #123", "IKEA #456", "CHEFSTORE 7583" all key to the brand.
  return (name || '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b\d+\b/g, ' ')  // standalone number tokens = store #s
    .replace(/\s+/g, ' ')
    .trim();
}

// The rule key for a charge: its normalized merchant, or a per-charge fallback
// when there's no name at all (so nameless charges don't collapse onto one
// shared rule). Bank/ACH rows carry the FULL NACHA descriptor in merchantName
// (walMerger stores merchantName || name), so extract the NAME: field first —
// that keys the rule on the name the tile DISPLAYS ("ADP"), keeps it stable
// across months (trace/date tokens vary per payment), and keeps "ADP" and
// "ADP Payroll Fees" as separate rules. Card charges have no NAME: field and
// pass through rawTxnName unchanged, so existing card rules keep their keys.
export function ruleKey(tx) {
  return normalizeMerchant(rawTxnName(tx.merchantName || tx.name || tx.description))
    || ('#' + tx.plaidTxId);
}

// Operator's per-merchant rules as a Map(ruleKey → category).
export async function getRules(userId) {
  const rows = await db.spendRule.where('userId').equals(userId).toArray();
  const m = new Map();
  for (const r of rows) if (VALID_IDS.has(r.category)) m.set(r.ruleKey, r.category);
  return m;
}

// Persist (category) or clear (null/'') a per-merchant rule.
export async function setRule(userId, key, category) {
  if (!category) { await db.spendRule.delete([userId, key]); return; }
  if (!VALID_IDS.has(category)) throw new Error('unknown spend category: ' + category);
  await db.spendRule.put({ userId, ruleKey: key, category, updatedAt: Date.now() });
}

// COGS charges: invoices already matched (by the reconciler) to a card charge.
// Returns Map(plaidTxId → { invoiceId, vendor }).
export async function getReconciledCogs(userId) {
  const invs = await db.invoices.where('userId').equals(userId).filter((i) => !!i.plaidTxId).toArray();
  const vendorIds = [...new Set(invs.map((i) => i.vendorId).filter(Boolean))];
  const vendors = vendorIds.length ? await db.vendors.where('vendorId').anyOf(vendorIds).toArray() : [];
  const vName = new Map(vendors.map((v) => [v.vendorId, v.name]));
  const byTx = new Map();
  for (const i of invs) byTx.set(i.plaidTxId, { invoiceId: i.invoiceId, vendor: vName.get(i.vendorId) || '' });
  return byTx;
}

// Load the keyword sets the guesser needs, once, so a caller can categorize many
// charges without re-reading the profile per charge.
export async function loadGuessKeywords() {
  const [rent, insurance, expense, tax] = await Promise.all([
    getRentKeywords(), getInsuranceKeywords(), getExpenseKeywords(), getTaxKeywords(),
  ]);
  return { rent, insurance, expense, tax };
}

// Best-effort category guess, reusing the report classifiers. Supplies has no
// keyword classifier (it's Claude-assisted elsewhere), so it's never guessed —
// the operator sets it once and the per-merchant rule remembers it.
export function guessCategory(tx, kw) {
  if (!kw) return null;
  // Tax keywords are operator-entered (no built-in defaults) — the strongest
  // signal we have, so they outrank the other guessers.
  if (kw.tax && classifyTax(tx, kw.tax)) return 'taxes';
  if (isRent(tx, kw.rent)) return 'rent_utilities';
  const exp = categorizeExpense(tx.merchantName, tx.description, kw.expense);
  if (exp === 'utility') return 'rent_utilities';
  if (exp === 'maintenance') return 'maintenance';
  if (isInsurance(tx, kw.insurance)) return 'insurance';
  return null;
}

// Effective category of a charge (see precedence at top). `reconciledTxIds` is a
// Set of plaidTxIds a scanned invoice matched. `rules` holds both per-charge
// overrides (keyed `tx:<plaidTxId>`) and per-merchant rules (keyed by ruleKey);
// the per-charge override wins so a one-off classification beats the brand rule.
export function effectiveCategory(tx, { reconciledTxIds, rules, keywords }) {
  if (reconciledTxIds && reconciledTxIds.has(tx.plaidTxId)) return 'cogs';
  const override = rules && rules.get('tx:' + tx.plaidTxId);
  if (override) return override;
  const rule = rules && rules.get(ruleKey(tx));
  if (rule) return rule;
  return guessCategory(tx, keywords);
}

/**
 * The OPERATOR-DECIDED category of a charge — reconciled COGS, per-charge
 * override, or per-merchant rule — and null otherwise. Unlike
 * effectiveCategory it NEVER falls back to the keyword guess: the cost
 * reports' own keyword pipelines keep owning un-ruled charges, and this only
 * carries the decisions that must override them (both ways: a charge
 * categorized INTO a report counts there even without a keyword match, and a
 * keyword match the operator rerouted elsewhere must NOT count).
 */
export function explicitCategory(tx, { reconciledTxIds, rules }) {
  if (reconciledTxIds && reconciledTxIds.has(tx.plaidTxId)) return 'cogs';
  const override = rules && rules.get('tx:' + tx.plaidTxId);
  if (override) return override;
  return (rules && rules.get(ruleKey(tx))) || null;
}

/** Load the context explicitCategory needs, once per screen. */
export async function loadExplicitContext(userId) {
  const [rules, reconciled] = await Promise.all([getRules(userId), getReconciledCogs(userId)]);
  return { rules, reconciledTxIds: new Set(reconciled.keys()) };
}

/**
 * Annotate transactions with their explicit category as `_spendCat` (absent
 * when the operator hasn't decided). Card charges stamp any sign (refunds net
 * out per category); depository rows stamp OUTFLOWS only — a deposit is never
 * spend, so a merchant rule must not relabel it. Un-ruled depository rows stay
 * unstamped and remain with each report's keyword pipeline.
 * Returns a new array; the input rows are not mutated.
 */
export function stampCardCategories(txs, creditSet, ctx) {
  if (!ctx) return txs || [];
  const cards = creditSet || new Set();
  return (txs || []).map((t) => {
    if (!cards.has(t.plaidAccountId) && (t.amountCents || 0) <= 0) return t;
    const cat = explicitCategory(t, ctx);
    return cat ? { ...t, _spendCat: cat } : t;
  });
}
